import { computed, DestroyRef, inject, Service, signal, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, timer } from 'rxjs';

import { InformationStatus } from '@ideals/types';
import { createUniqueId } from '@ideals/utils';

import { Alert, Alerts, MessageConfig } from './models';

export const AUTO_CLOSE_TIMEOUT = 2000;
export const TRANSITION_TIMEOUT = 500;

@Service()
export class PageMessagesService {
  readonly #destroyRef = inject(DestroyRef);
  readonly #messages = signal<Alerts>([]);
  readonly #timerSubscriptions = new Map<string, Subscription>();

  readonly messages = computed(() => this.#messages());

  close(alert: Alert): void {
    const subscription = this.#timerSubscriptions.get(alert.id);

    if (subscription) {
      subscription.unsubscribe();
      this.#timerSubscriptions.delete(alert.id);
    }

    this.#messages.update((messages) => messages.filter((message) => message.id !== alert.id));
  }

  error({
    text,
    autoClose = true,
    timeToClose,
    preventDuplicates,
    htmlCaption,
    htmlCaptionParams,
    template,
    customClass,
  }: MessageConfig): void {
    this.#addMessage(
      text,
      InformationStatus.Danger,
      autoClose,
      timeToClose,
      preventDuplicates,
      htmlCaption,
      htmlCaptionParams,
      customClass,
      template
    );
  }

  info({
    text,
    autoClose = true,
    timeToClose,
    preventDuplicates,
    htmlCaption,
    htmlCaptionParams,
    template,
    customClass,
  }: MessageConfig): void {
    this.#addMessage(
      text,
      InformationStatus.Information,
      autoClose,
      timeToClose,
      preventDuplicates,
      htmlCaption,
      htmlCaptionParams,
      customClass,
      template
    );
  }

  success({
    text,
    autoClose = true,
    timeToClose,
    preventDuplicates,
    htmlCaption,
    htmlCaptionParams,
    template,
    customClass,
  }: MessageConfig): void {
    this.#addMessage(
      text,
      InformationStatus.Success,
      autoClose,
      timeToClose,
      preventDuplicates,
      htmlCaption,
      htmlCaptionParams,
      customClass,
      template
    );
  }

  warning({
    text,
    autoClose = true,
    timeToClose,
    preventDuplicates,
    htmlCaption,
    htmlCaptionParams,
    template,
    customClass,
  }: MessageConfig): void {
    this.#addMessage(
      text,
      InformationStatus.Warning,
      autoClose,
      timeToClose,
      preventDuplicates,
      htmlCaption,
      htmlCaptionParams,
      customClass,
      template
    );
  }

  #addMessage(
    caption: string,
    type: InformationStatus,
    autoClose: boolean = true,
    closeTimeout: number = AUTO_CLOSE_TIMEOUT,
    preventDuplicates: boolean = true,
    htmlCaption?: string,
    htmlCaptionParams?: Record<string, unknown>,
    customClass?: string,
    template?: TemplateRef<unknown>
  ): void {
    if (preventDuplicates && this.#hasDuplicates(caption)) {
      return;
    }

    const alert = {
      id: createUniqueId(),
      caption,
      type,
      htmlCaption,
      htmlCaptionParams,
      customClass,
      template,
    };

    if (autoClose) {
      const subscription = timer(closeTimeout)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => this.close(alert));

      this.#timerSubscriptions.set(alert.id, subscription);
    }

    this.#messages.update((messages) => [...messages, alert]);
  }

  #hasDuplicates(caption: string): boolean {
    return this.#messages().some((message) => message.caption === caption);
  }
}
